/**
 * /admin/payouts — pay freelancers through Cashfree Payouts.
 *
 * Three tabs:
 *  - Pay run:    one week (Mon-Sun). Build it, check each line, press Pay all.
 *  - Approvals:  coded matches waiting for approval; add a match by hand.
 *  - Freelancers: who we pay, how much, and where. New or changed bank
 *                details must pass a ₹1 test transfer before pay goes out.
 *
 * Money arrives from the API in paise and is typed in rupees.
 */
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Loader2, Send, Check, X, Pause, Play, RotateCcw, ArrowRight,
  Download, Plus, ShieldCheck, ShieldAlert, ChevronDown, ChevronRight, Search,
} from "lucide-react";
import {
  getPayoutOverview, listFreelancers, createFreelancer, updateFreelancer,
  verifyFreelancer, listWorkItems, addManualWorkItem, syncWorkItems,
  reviewWorkItems, listPayRuns, buildPayRun, getPayRun, payRun, refreshPayRun,
  exportPayRun, updatePayLine, payLineAction,
} from "../../api/admin.payouts.api";
import { searchCreditUsers } from "../../api/admin.extras.api";
import { useToast } from "../../components/common/ToastHost";

const rs = (paise) =>
  `₹${((paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const errMsg = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

const STATE_STYLE = {
  DRAFT: "bg-bg-secondary text-text-secondary",
  QUEUED: "bg-blue-100 text-blue-700",
  PENDING: "bg-blue-100 text-blue-700",
  SUCCESS: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
  REVERSED: "bg-red-100 text-red-700",
  CANCELLED: "bg-bg-secondary text-text-secondary",
};

const TDS_OPTIONS = [
  ["NONE", "No TDS"],
  ["194C_IND", "194C contractor - individual (1%)"],
  ["194C_OTHER", "194C contractor - firm/company (2%)"],
  ["194J", "194J professional fees (10%)"],
];

const btn = "px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed";
const btnPrimary = "px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed";
const input = "px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary";
const th = "px-4 py-2.5";
const td = "px-4 py-2.5";

export default function AdminPayouts() {
  const toast = useToast();
  const [tab, setTab] = useState("run");
  const [overview, setOverview] = useState(null);

  async function loadOverview() {
    try {
      const { data } = await getPayoutOverview();
      setOverview(data);
    } catch (err) {
      toast.error(errMsg(err, "Couldn't load payouts."));
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOverview(); }, []);

  const tabs = [
    ["run", "Pay run"],
    ["approvals", `Approvals${overview?.pending_approval ? ` (${overview.pending_approval})` : ""}`],
    ["freelancers", `Freelancers${overview?.unverified_freelancers ? ` (${overview.unverified_freelancers} unverified)` : ""}`],
  ];

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Payouts</h1>
          <p className="text-sm text-text-secondary mt-1">
            Pay freelancers every week through Cashfree Payouts.
          </p>
        </div>
        {overview && (
          <div className="flex items-center gap-3 text-sm">
            {overview.environment !== "production" && (
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-medium">
                Sandbox - no real money moves
              </span>
            )}
            <span className="text-text-secondary">
              Wallet balance{" "}
              <strong className="text-text-primary">
                {overview.balance_paise == null ? "unavailable" : rs(overview.balance_paise)}
              </strong>
            </span>
          </div>
        )}
      </header>

      {overview && !overview.configured && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-4 text-sm">
          Cashfree Payouts isn't connected yet. You can add freelancers and build pay runs, but
          paying and account checks stay off until the Payouts API keys are set on the server.
        </div>
      )}

      <nav className="flex gap-1 border-b border-border-default">
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === key
              ? "border-blue-600 text-blue-600 font-medium"
              : "border-transparent text-text-secondary hover:text-text-primary"}`}>
            {label}
          </button>
        ))}
      </nav>

      {tab === "run" && <PayRunTab overview={overview} onChange={loadOverview} />}
      {tab === "approvals" && <ApprovalsTab onChange={loadOverview} />}
      {tab === "freelancers" && <FreelancersTab configured={overview?.configured} onChange={loadOverview} />}
    </div>
  );
}

// ── Pay run ─────────────────────────────────────────────────────────

function PayRunTab({ overview, onChange }) {
  const toast = useToast();
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [week, setWeek] = useState("");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    if (overview?.suggested_week_start && !week) setWeek(overview.suggested_week_start);
  }, [overview, week]);

  async function loadRuns(selectId) {
    try {
      const { data } = await listPayRuns();
      setRuns(data.results || []);
      const id = selectId || run?.id || data.results?.[0]?.id;
      if (id) setRun((await getPayRun(id)).data);
    } catch (err) {
      toast.error(errMsg(err, "Couldn't load pay runs."));
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRuns(); }, []);

  async function act(label, fn) {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      toast.error(errMsg(err, "That didn't work."));
    } finally {
      setBusy("");
    }
  }

  const build = () => act("build", async () => {
    const { data } = await buildPayRun(week);
    toast.success(`Week of ${fmtDate(data.week_start)} is ready to check.`);
    await loadRuns(data.id);
  });

  const isDraft = run?.status === "DRAFT";
  const toPay = (run?.lines || []).filter((l) => !l.held && l.net_paise >= 100);
  const unverified = toPay.filter((l) => !l.freelancer.verified);

  const pay = () => act("pay", async () => {
    const total = run.payable_total_paise;
    const ok = window.confirm(
      `Pay ${toPay.length} freelancer${toPay.length === 1 ? "" : "s"} a total of ${rs(total)} now?\n\n`
      + "Money leaves the Cashfree wallet straight away. This can't be undone.",
    );
    if (!ok) return;
    const { data } = await payRun(run.id, total);
    setRun(data);
    toast.success("Payments sent. Statuses update as banks confirm.");
    loadRuns(run.id);
    onChange();
  });

  const refresh = () => act("refresh", async () => {
    const { data } = await refreshPayRun(run.id);
    setRun(data);
    loadRuns(run.id);
  });

  const exportCsv = () => act("export", async () => {
    const { data } = await exportPayRun(run.id);
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `freelancer-pay-${run.week_start}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  });

  async function patchLine(line, body) {
    try {
      await updatePayLine(line.id, body);
      setRun((await getPayRun(run.id)).data);
    } catch (err) {
      toast.error(errMsg(err, "Couldn't update the line."));
    }
  }

  async function lineAction(line, action) {
    const msg = action === "retry"
      ? `Send ${rs(line.net_paise)} to ${line.freelancer.name} again?`
      : `Give up on this payment? ${line.freelancer.name}'s matches go into the next run `
        + "(weekly pay for this week would need an adjustment).";
    if (!window.confirm(msg)) return;
    await act(`line-${line.id}`, async () => {
      await payLineAction(line.id, action);
      setRun((await getPayRun(run.id)).data);
      loadRuns(run.id);
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium mb-1 text-text-secondary">Week starting (Monday)</label>
          <input type="date" value={week} onChange={(e) => setWeek(e.target.value)} className={input} />
        </div>
        <button onClick={build} disabled={!week || !!busy} className={btn}>
          {busy === "build" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Build / refresh week
        </button>
        {runs.length > 0 && (
          <div className="ml-auto">
            <label className="block text-xs font-medium mb-1 text-text-secondary">Past runs</label>
            <select value={run?.id || ""} onChange={(e) => loadRuns(e.target.value)} className={input}>
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {fmtDate(r.week_start)} · {r.status_label} · {rs(r.total_net_paise)}
                  {r.failed_count ? ` · ${r.failed_count} failed` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!run && (
        <p className="text-sm text-text-secondary">
          No pay runs yet. Pick a finished week and press Build.
        </p>
      )}

      {run && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">
              Week of {fmtDate(run.week_start)} - {fmtDate(run.week_end)}
              <span className="ml-2 text-sm font-normal text-text-secondary">{run.status_label}</span>
            </h2>
            <div className="flex gap-2">
              {run.status !== "DRAFT" && (
                <button onClick={refresh} disabled={!!busy} className={btn}>
                  <RefreshCw className={`w-4 h-4 ${busy === "refresh" ? "animate-spin" : ""}`} /> Check status
                </button>
              )}
              <button onClick={exportCsv} disabled={!!busy} className={btn}>
                <Download className="w-4 h-4" /> CSV
              </button>
            </div>
          </div>

          <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
                <tr>
                  <th className={th}>Freelancer</th>
                  <th className={th}>Work</th>
                  <th className={`${th} text-right`}>Earned</th>
                  <th className={th}>Adjustment</th>
                  <th className={`${th} text-right`}>TDS</th>
                  <th className={`${th} text-right`}>Pay</th>
                  <th className={th}>Status</th>
                  <th className={th}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {run.lines.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                    Nobody to pay this week. Approve matches or add freelancers, then build again.
                  </td></tr>
                )}
                {run.lines.map((l) => (
                  <LineRow
                    // Remount when the saved adjustment changes so the inputs reset.
                    key={`${l.id}:${l.adjustment_paise}:${l.adjustment_note}`} line={l} editable={isDraft && l.state === "DRAFT"}
                    open={!!open[l.id]} onToggle={() => setOpen((o) => ({ ...o, [l.id]: !o[l.id] }))}
                    onPatch={(body) => patchLine(l, body)} onAction={(a) => lineAction(l, a)}
                    busy={busy === `line-${l.id}`} />
                ))}
              </tbody>
            </table>
          </div>

          {isDraft && run.lines.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-4">
              {unverified.length > 0 && (
                <span className="text-sm text-red-600 flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4" />
                  Not verified: {unverified.map((l) => l.freelancer.name).join(", ")}. Verify or hold them.
                </span>
              )}
              <span className="text-sm">
                {toPay.length} to pay · <strong className="text-base">{rs(run.payable_total_paise)}</strong>
              </span>
              <button onClick={pay}
                disabled={!!busy || !overview?.configured || toPay.length === 0 || unverified.length > 0}
                className={btnPrimary}>
                {busy === "pay" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Pay all {rs(run.payable_total_paise)}
              </button>
            </div>
          )}
          {run.status !== "DRAFT" && (
            <p className="text-sm text-text-secondary text-right">
              Paid {rs(run.paid_paise)} of {rs(run.total_net_paise)}
              {run.paid_by && ` · sent by ${run.paid_by} on ${fmtDate(run.paid_at)}`}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function LineRow({ line: l, editable, open, onToggle, onPatch, onAction, busy }) {
  const [adj, setAdj] = useState(l.adjustment_paise ? String(l.adjustment_paise / 100) : "");
  const [note, setNote] = useState(l.adjustment_note || "");

  const dirty = (adj || "0") !== String((l.adjustment_paise || 0) / 100) || note !== (l.adjustment_note || "");
  const perMatch = l.freelancer.pay_type === "PER_MATCH";

  return (
    <>
      <tr className={l.held ? "opacity-60" : ""}>
        <td className={td}>
          <div className="font-medium flex items-center gap-1.5">
            {l.freelancer.name}
            {l.freelancer.verified
              ? <ShieldCheck className="w-3.5 h-3.5 text-green-600" title="Account verified" />
              : <ShieldAlert className="w-3.5 h-3.5 text-red-600" title="Account not verified" />}
          </div>
          <div className="text-xs text-text-secondary">{l.freelancer.payout_target}</div>
        </td>
        <td className={td}>
          {perMatch ? (
            <button onClick={onToggle} className="flex items-center gap-1 hover:underline">
              {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {l.match_count} match{l.match_count === 1 ? "" : "es"}
            </button>
          ) : (
            <span>{l.days_paid} of 7 days</span>
          )}
        </td>
        <td className={`${td} text-right`}>{rs(l.gross_paise)}</td>
        <td className={td}>
          {editable ? (
            <div className="flex items-center gap-1.5">
              <input value={adj} onChange={(e) => setAdj(e.target.value)} placeholder="0"
                className={`${input} w-24 py-1`} title="Rupees. Use - for a deduction." />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why"
                className={`${input} w-32 py-1`} />
              {dirty && (
                <button onClick={() => onPatch({ adjustment: adj || "0", adjustment_note: note })}
                  className="p-1 rounded hover:bg-bg-secondary" title="Save">
                  <Check className="w-4 h-4 text-green-600" />
                </button>
              )}
            </div>
          ) : l.adjustment_paise ? (
            <span>{rs(l.adjustment_paise)} <span className="text-xs text-text-secondary">{l.adjustment_note}</span></span>
          ) : <span className="text-text-secondary">-</span>}
        </td>
        <td className={`${td} text-right`} title={l.tds_note}>
          {l.tds_paise ? rs(l.tds_paise) : <span className="text-text-secondary">-</span>}
        </td>
        <td className={`${td} text-right font-semibold`}>{rs(l.net_paise)}</td>
        <td className={td}>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATE_STYLE[l.state] || ""}`}>
            {l.held && l.state === "DRAFT" ? "On hold" : l.state_label}
          </span>
          {(l.status_message || l.utr) && (
            <div className="text-xs text-text-secondary mt-1 max-w-xs">
              {l.utr ? `UTR ${l.utr}` : l.status_message}
            </div>
          )}
        </td>
        <td className={`${td} whitespace-nowrap`}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {!busy && editable && (
            <button onClick={() => onPatch({ held: !l.held })} className="p-1 rounded hover:bg-bg-secondary"
              title={l.held ? "Pay this week" : "Hold - don't pay this week"}>
              {l.held ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
          )}
          {!busy && (l.state === "FAILED" || l.state === "REVERSED") && (
            <span className="flex gap-1">
              <button onClick={() => onAction("retry")} className="p-1 rounded hover:bg-bg-secondary" title="Send again">
                <RotateCcw className="w-4 h-4" />
              </button>
              <button onClick={() => onAction("cancel")} className="p-1 rounded hover:bg-bg-secondary" title="Move to next run">
                <ArrowRight className="w-4 h-4" />
              </button>
            </span>
          )}
        </td>
      </tr>
      {open && perMatch && (
        <tr className="bg-bg-secondary/50">
          <td colSpan={8} className="px-10 py-2">
            {l.items.length === 0 ? <span className="text-text-secondary text-xs">No matches.</span> : (
              <ul className="text-xs space-y-1">
                {l.items.map((i) => (
                  <li key={i.id} className="flex justify-between max-w-xl">
                    <span>{i.title} <span className="text-text-secondary">· {i.kind_label}</span></span>
                    <span>{rs(i.amount_paise)}</span>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Approvals ───────────────────────────────────────────────────────

function ApprovalsTab({ onChange }) {
  const toast = useToast();
  const [filter, setFilter] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState("");
  const [freelancers, setFreelancers] = useState([]);
  const [manual, setManual] = useState({ freelancer_id: "", title: "", amount: "" });

  async function load() {
    setLoading(true);
    try {
      const { data } = await listWorkItems({ status: filter });
      setItems(data.results || []);
      setPicked({});
    } catch (err) {
      toast.error(errMsg(err, "Couldn't load matches."));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    listFreelancers().then(({ data }) =>
      setFreelancers((data.results || []).filter((f) => f.pay_type === "PER_MATCH")))
      .catch(() => {});
  }, []);

  const ids = Object.keys(picked).filter((k) => picked[k]);

  async function review(action, only) {
    const target = only ? [only] : ids;
    if (!target.length) return;
    setBusy(action);
    try {
      const { data } = await reviewWorkItems({ ids: target, action });
      (data.errors || []).forEach((e) => toast.error(e));
      if (data.results?.length) toast.success(`${data.results.length} updated.`);
      await load();
      onChange();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't update."));
    } finally {
      setBusy("");
    }
  }

  async function sync() {
    setBusy("sync");
    try {
      const { data } = await syncWorkItems();
      toast.success(data.added ? `Found ${data.added} new match${data.added === 1 ? "" : "es"}.` : "No new matches.");
      await load();
      onChange();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't look for matches."));
    } finally {
      setBusy("");
    }
  }

  async function addManual(e) {
    e.preventDefault();
    setBusy("manual");
    try {
      await addManualWorkItem(manual);
      toast.success("Added and approved.");
      setManual({ freelancer_id: manual.freelancer_id, title: "", amount: "" });
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't add it."));
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className={input}>
          <option value="PENDING">Waiting for approval</option>
          <option value="UNPAID">Approved, not paid yet</option>
          <option value="PAID">Paid</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <button onClick={sync} disabled={!!busy} className={btn}>
          {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Find new matches
        </button>
        {filter === "PENDING" && ids.length > 0 && (
          <div className="flex gap-2 ml-auto">
            <button onClick={() => review("approve")} disabled={!!busy} className={btnPrimary}>
              <Check className="w-4 h-4" /> Approve {ids.length}
            </button>
            <button onClick={() => review("reject")} disabled={!!busy} className={btn}>
              <X className="w-4 h-4" /> Reject {ids.length}
            </button>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              {filter === "PENDING" && (
                <th className={th}>
                  <input type="checkbox"
                    checked={items.length > 0 && ids.length === items.length}
                    onChange={(e) => setPicked(Object.fromEntries(items.map((i) => [i.id, e.target.checked])))} />
                </th>
              )}
              <th className={th}>Match</th>
              <th className={th}>Freelancer</th>
              <th className={th}>Type</th>
              <th className={th}>Done</th>
              <th className={`${th} text-right`}>Pay</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary">Nothing here.</td></tr>
            )}
            {!loading && items.map((i) => (
              <tr key={i.id} className={i.on_hold ? "opacity-60" : ""}>
                {filter === "PENDING" && (
                  <td className={td}>
                    <input type="checkbox" checked={!!picked[i.id]}
                      onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))} />
                  </td>
                )}
                <td className={td}>
                  <div className="font-medium">{i.title}</div>
                  {i.review_note && <div className="text-xs text-text-secondary">{i.review_note}</div>}
                </td>
                <td className={td}>{i.freelancer.name}</td>
                <td className={td}>{i.kind_label}</td>
                <td className={`${td} whitespace-nowrap`}>{fmtDate(i.completed_at)}</td>
                <td className={`${td} text-right`}>{rs(i.amount_paise)}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {filter === "UNPAID" && (
                    <button onClick={() => review(i.on_hold ? "release" : "hold", i.id)} disabled={!!busy}
                      className="text-xs px-2 py-1 rounded border border-border-default hover:bg-bg-secondary">
                      {i.on_hold ? "Release" : "Hold"}
                    </button>
                  )}
                  {filter === "REJECTED" && (
                    <button onClick={() => review("reopen", i.id)} disabled={!!busy}
                      className="text-xs px-2 py-1 rounded border border-border-default hover:bg-bg-secondary">
                      Reopen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={addManual} className="rounded-md border border-border-default bg-bg-primary p-4 space-y-3 max-w-3xl">
        <h3 className="font-medium">Add a match by hand</h3>
        <p className="text-xs text-text-secondary">
          For work ZCD can't see on its own, such as Ball QC done in the ingest app. It's approved straight away.
        </p>
        <div className="flex flex-wrap gap-2">
          <select required value={manual.freelancer_id}
            onChange={(e) => setManual({ ...manual, freelancer_id: e.target.value })} className={input}>
            <option value="">Freelancer…</option>
            {freelancers.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <input required value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })}
            placeholder="e.g. Ball QC · IPL 2026 Match 12" className={`${input} flex-1 min-w-[220px]`} />
          <input value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })}
            placeholder="₹ (their rate)" className={`${input} w-32`} />
          <button type="submit" disabled={busy === "manual"} className={btn}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Freelancers ─────────────────────────────────────────────────────

const EMPTY = {
  name: "", email: "", phone: "", pan: "", user: null, pay_type: "PER_MATCH", rate: "",
  tds_section: "NONE", payout_mode: "BANK", bank_account_number: "", bank_ifsc: "",
  upi_vpa: "", start_date: new Date().toISOString().slice(0, 10), end_date: "",
  is_active: true, notes: "",
};

function FreelancersTab({ configured, onChange }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | {id?, ...form}
  const [busy, setBusy] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await listFreelancers();
      setRows(data.results || []);
    } catch (err) {
      toast.error(errMsg(err, "Couldn't load freelancers."));
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  function edit(f) {
    setEditing({
      ...EMPTY, ...f, rate: String(f.rate_paise / 100), bank_account_number: "",
      end_date: f.end_date || "", pan: f.pan || "",
    });
  }

  async function verify(f) {
    if (!window.confirm(`Send ₹1 to ${f.name} (${f.payout_target}) to check the account?`)) return;
    setBusy(f.id);
    try {
      await verifyFreelancer(f.id);
      toast.success("₹1 sent. It usually confirms within a minute - press Refresh.");
      await load();
      onChange();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't send the test transfer."));
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <button onClick={() => setEditing({ ...EMPTY })} className={btnPrimary}>
          <Plus className="w-4 h-4" /> Add freelancer
        </button>
        <button onClick={load} className={btn}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {editing && (
        <FreelancerForm initial={editing} onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onChange(); }} />
      )}

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className={th}>Name</th>
              <th className={th}>Pay</th>
              <th className={th}>Paid to</th>
              <th className={th}>Account check</th>
              <th className={th}>TDS</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary">No freelancers yet.</td></tr>
            )}
            {rows.map((f) => (
              <tr key={f.id} className={f.is_active ? "" : "opacity-50"}>
                <td className={td}>
                  <div className="font-medium">{f.name}{!f.is_active && " (inactive)"}</div>
                  <div className="text-xs text-text-secondary">{f.user?.email || f.email || "no ZCD login"}</div>
                </td>
                <td className={td}>
                  {rs(f.rate_paise)} {f.pay_type === "PER_MATCH" ? "/ match" : "/ week"}
                </td>
                <td className={td}>{f.payout_target || <span className="text-red-600">missing</span>}</td>
                <td className={td}>
                  {f.verified ? (
                    <span className="text-green-700 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Verified</span>
                  ) : (
                    <div className="space-y-1">
                      <span className="text-red-600 flex items-center gap-1">
                        <ShieldAlert className="w-4 h-4" />
                        {f.verify_status ? `Test ₹1: ${f.verify_status.toLowerCase()}` : "Not verified"}
                      </span>
                      {f.verify_message && <div className="text-xs text-text-secondary max-w-xs">{f.verify_message}</div>}
                      <button onClick={() => verify(f)} disabled={!configured || busy === f.id}
                        className="text-xs px-2 py-1 rounded border border-border-default hover:bg-bg-secondary disabled:opacity-50">
                        {busy === f.id ? "Sending…" : "Send ₹1 check"}
                      </button>
                    </div>
                  )}
                </td>
                <td className={td}>{TDS_OPTIONS.find(([k]) => k === f.tds_section)?.[1] || f.tds_section}</td>
                <td className={td}>
                  <button onClick={() => edit(f)} className="text-xs px-2 py-1 rounded border border-border-default hover:bg-bg-secondary">
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FreelancerForm({ initial, onCancel, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const editingId = initial.id;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => {
    if (q.trim().length < 2) { setUsers([]); return undefined; }
    const t = setTimeout(() => {
      searchCreditUsers(q.trim()).then(({ data }) => setUsers(data.results || [])).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const body = useMemo(() => {
    const b = {
      name: form.name, email: form.email, phone: form.phone, pan: form.pan,
      user_id: form.user?.id ?? null, pay_type: form.pay_type, rate: form.rate,
      tds_section: form.tds_section, payout_mode: form.payout_mode,
      bank_ifsc: form.bank_ifsc, upi_vpa: form.upi_vpa,
      start_date: form.start_date, end_date: form.end_date || null,
      is_active: form.is_active, notes: form.notes,
    };
    // The saved account number never comes back; only send a new one.
    if (form.bank_account_number) b.bank_account_number = form.bank_account_number;
    return b;
  }, [form]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await updateFreelancer(editingId, body);
      else await createFreelancer(body);
      toast.success(editingId ? "Saved." : "Freelancer added. Send the ₹1 check before their first pay.");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save."));
    } finally {
      setSaving(false);
    }
  }

  const field = (label, el, hint) => (
    <label className="block">
      <span className="block text-xs font-medium mb-1 text-text-secondary">{label}</span>
      {el}
      {hint && <span className="block text-xs text-text-secondary mt-1">{hint}</span>}
    </label>
  );

  return (
    <form onSubmit={save} className="rounded-md border border-border-default bg-bg-primary p-5 space-y-4 max-w-4xl">
      <h3 className="font-semibold">{editingId ? `Edit ${initial.name}` : "Add freelancer"}</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {field("Name (as on bank account)", <input required value={form.name} onChange={set("name")} className={`${input} w-full`} />)}
        {field("Email (payslips)", <input type="email" value={form.email} onChange={set("email")} className={`${input} w-full`} />)}
        {field("Phone", <input value={form.phone} onChange={set("phone")} className={`${input} w-full`} />)}
        {field("PAN", <input value={form.pan} onChange={set("pan")} className={`${input} w-full uppercase`} maxLength={10} />,
          "Without a PAN, TDS is 20%.")}
        {field("ZCD login", form.user ? (
          <div className={`${input} flex items-center justify-between`}>
            <span className="truncate">{form.user.email}</span>
            <button type="button" onClick={() => setForm((f) => ({ ...f, user: null }))}><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="relative">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" className={`${input} w-full`} />
            {users.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-border-default bg-bg-primary shadow-lg">
                {users.map((u) => (
                  <li key={u.id}>
                    <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary"
                      onClick={() => { setForm((f) => ({ ...f, user: { id: u.id, email: u.email } })); setUsers([]); setQ(""); }}>
                      {u.name} <span className="text-text-secondary">· {u.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ), "Needed for per-match pay, so their matches are found.")}
        {field("TDS", (
          <select value={form.tds_section} onChange={set("tds_section")} className={`${input} w-full`}>
            {TDS_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {field("Paid", (
          <select value={form.pay_type} onChange={set("pay_type")} className={`${input} w-full`}>
            <option value="PER_MATCH">Per approved match</option>
            <option value="WEEKLY">Per week</option>
          </select>
        ))}
        {field(form.pay_type === "WEEKLY" ? "Amount per week (₹)" : "Amount per match (₹)",
          <input required value={form.rate} onChange={set("rate")} className={`${input} w-full`} />)}
        {field("Start date", <input required type="date" value={form.start_date} onChange={set("start_date")} className={`${input} w-full`} />)}
        {field("End date (optional)", <input type="date" value={form.end_date} onChange={set("end_date")} className={`${input} w-full`} />)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {field("Pay into", (
          <select value={form.payout_mode} onChange={set("payout_mode")} className={`${input} w-full`}>
            <option value="BANK">Bank account</option>
            <option value="UPI">UPI</option>
          </select>
        ))}
        {form.payout_mode === "BANK" ? (
          <>
            {field("Account number", (
              <input value={form.bank_account_number} onChange={set("bank_account_number")}
                placeholder={editingId && initial.bank_account_last4 ? `XXXX${initial.bank_account_last4} - type to change` : ""}
                required={!editingId || !initial.bank_account_last4}
                className={`${input} w-full`} />
            ))}
            {field("IFSC", <input required value={form.bank_ifsc} onChange={set("bank_ifsc")} className={`${input} w-full uppercase`} maxLength={11} />)}
          </>
        ) : (
          field("UPI ID", <input required value={form.upi_vpa} onChange={set("upi_vpa")} placeholder="name@okhdfcbank" className={`${input} w-full`} />)
        )}
      </div>

      {editingId && (
        <p className="text-xs text-amber-700">Changing the name or payout details un-verifies the account until a new ₹1 check succeeds.</p>
      )}

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={set("is_active")} /> Active
        </label>
        <input value={form.notes} onChange={set("notes")} placeholder="Notes" className={`${input} flex-1`} />
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
        <button type="button" onClick={onCancel} className={btn}>Cancel</button>
      </div>
    </form>
  );
}
